/**
 * Redraws the current map view as a single flat image, for the print sheet.
 *
 * The live map DOM can't be printed directly (Leaflet positions tiles with CSS
 * transforms, the overlays are SVG, the controls would come along for the ride,
 * and the container is sized for the screen rather than the page), so the map is
 * redrawn from scratch onto an offscreen canvas: the tile grid for the current
 * extent, then the same result geometry the map is showing, using the shared
 * styles in utils/mapMarkerStyles.
 *
 * Tiles come from one zoom level deeper than the screen is showing, so the
 * printed map is sharper than a screen capture would be. The page itself is laid
 * out in HTML by MapPrintSheet and handed to the browser's own print dialog.
 */

import L from 'leaflet';
import {
  MAP_MAX_NATIVE_ZOOM,
  TILE_SIZE,
  TILE_CROSS_ORIGIN,
  aerialTileUrl,
  osTileUrl,
  printTileUrl,
} from './mapTiles';
import {
  HEX_ANCHOR,
  HEX_SIZE,
  HEX_ZOOM_THRESHOLD,
  LOCATION_OUTLINE_STYLE,
  PIN_ANCHOR,
  PIN_SIZE,
  POLYGON_DEFAULT_STYLE,
  POLYGON_FOCUS_CASING_STYLE,
  POLYGON_FOCUS_STYLE,
  CLUSTER_GRID_COLOR,
  buildHexSvg,
  buildPinSvg,
  clusterBadgeStyle,
  hexAnchorFor,
} from './mapMarkerStyles';

// One extra zoom level of tiles doubles the print resolution (~200dpi at A4
// width) for the same extent. Capped by what the tile proxy actually holds.
const PRINT_ZOOM_BOOST = 1;

const MAX_CANVAS_EDGE = 4200;
const MAX_TILES = 220;

function loadImage(src, crossOrigin) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (crossOrigin) img.crossOrigin = crossOrigin;
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Could not load ${src}`));
    img.src = src;
  });
}

function svgToImage(svg) {
  return loadImage(
    `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`,
  );
}

async function loadTile(url) {
  try {
    return await loadImage(printTileUrl(url), TILE_CROSS_ORIGIN);
  } catch (e) {
    return null;
  }
}

function hexToRgb(hex) {
  const value = hex.replace('#', '');
  const full =
    value.length === 3
      ? value
          .split('')
          .map(c => c + c)
          .join('')
      : value;
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  };
}

function rgba(hex, alpha) {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * The projection for the render: canvas pixels are the map's pixel space at
 * pixelZoom, translated so the view's north-west corner is the origin.
 */
function createProjection(map, pixelZoom) {
  const bounds = map.getBounds();
  const origin = map.project(bounds.getNorthWest(), pixelZoom);
  const farCorner = map.project(bounds.getSouthEast(), pixelZoom);

  return {
    pixelZoom,
    origin,
    width: Math.max(1, Math.round(farCorner.x - origin.x)),
    height: Math.max(1, Math.round(farCorner.y - origin.y)),
    toCanvas(latLng) {
      const point = map.project(L.latLng(latLng), pixelZoom);
      return { x: point.x - origin.x, y: point.y - origin.y };
    },
  };
}

/**
 * How big to draw, and which tiles to draw it from.
 *
 * pixelZoom sets the canvas resolution and is never below the on-screen zoom,
 * so the print is at least as detailed as the screen. tileZoom is capped at
 * what the proxy actually holds, and any shortfall is made up by drawing each
 * tile larger — the same upscaling Leaflet does past maxNativeZoom.
 */
function chooseZooms(map) {
  const zoom = map.getZoom();
  const boosted = zoom + PRINT_ZOOM_BOOST;

  const projection = createProjection(map, boosted);
  const tileSpan =
    TILE_SIZE * 2 ** (boosted - Math.min(boosted, MAP_MAX_NATIVE_ZOOM));
  const tileCount =
    Math.ceil(projection.width / tileSpan + 1) *
    Math.ceil(projection.height / tileSpan + 1);

  const withinBudget =
    Math.max(projection.width, projection.height) <= MAX_CANVAS_EDGE &&
    tileCount <= MAX_TILES;

  const pixelZoom = withinBudget ? boosted : zoom;

  return { pixelZoom, tileZoom: Math.min(pixelZoom, MAP_MAX_NATIVE_ZOOM) };
}

function tileUrlFor(map, basemap, tileZoom, x, y) {
  if (basemap.type === 'aerial') {
    const { crs } = map.options;
    const nw = map.unproject(L.point(x * TILE_SIZE, y * TILE_SIZE), tileZoom);
    const se = map.unproject(
      L.point((x + 1) * TILE_SIZE, (y + 1) * TILE_SIZE),
      tileZoom,
    );
    return aerialTileUrl(
      basemap.layer,
      L.bounds(crs.project(nw), crs.project(se)),
    );
  }

  return osTileUrl(basemap.layer, tileZoom, x, y);
}

async function drawBasemap(ctx, map, basemap, projection, tileZoom) {
  const { origin } = projection;
  // A tile's footprint in canvas pixels: 256 at native, doubled per level the
  // canvas resolution runs ahead of the available tiles.
  const tileSpan = TILE_SIZE * 2 ** (projection.pixelZoom - tileZoom);

  const firstX = Math.floor(origin.x / tileSpan);
  const lastX = Math.floor((origin.x + projection.width) / tileSpan);
  const firstY = Math.floor(origin.y / tileSpan);
  const lastY = Math.floor((origin.y + projection.height) / tileSpan);

  const requests = [];
  for (let x = firstX; x <= lastX; x += 1) {
    for (let y = firstY; y <= lastY; y += 1) {
      requests.push({
        x,
        y,
        url: tileUrlFor(map, basemap, tileZoom, x, y),
      });
    }
  }

  const tiles = await Promise.all(requests.map(t => loadTile(t.url)));

  tiles.forEach((image, index) => {
    if (!image) return;
    const { x, y } = requests[index];
    ctx.drawImage(
      image,
      Math.round(x * tileSpan - origin.x),
      Math.round(y * tileSpan - origin.y),
      Math.ceil(tileSpan),
      Math.ceil(tileSpan),
    );
  });
}

// Leaflet latlng structures nest to different depths (ring, polygon-with-holes,
// multipolygon), so flatten to a list of rings for drawing.
function collectRings(latLngs, rings = []) {
  if (!Array.isArray(latLngs) || latLngs.length === 0) return rings;

  const first = latLngs[0];
  const isRing =
    Array.isArray(first) && typeof first[0] === 'number'
      ? true
      : first && typeof first.lat === 'number';

  if (isRing) {
    rings.push(latLngs);
    return rings;
  }

  latLngs.forEach(child => collectRings(child, rings));
  return rings;
}

function tracePath(ctx, projection, latLngs) {
  ctx.beginPath();
  collectRings(latLngs).forEach(ring => {
    ring.forEach((latLng, index) => {
      const { x, y } = projection.toCanvas(latLng);
      if (index === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });
    ctx.closePath();
  });
}

function drawShape(ctx, projection, latLngs, style, lineScale) {
  tracePath(ctx, projection, latLngs);

  if (style.fillColor && style.fill !== false) {
    ctx.fillStyle = rgba(style.fillColor, style.fillOpacity ?? 0.2);
    ctx.fill('evenodd');
  }

  if (style.weight) {
    ctx.strokeStyle = style.color;
    ctx.lineWidth = style.weight * lineScale;
    ctx.lineJoin = 'round';
    ctx.stroke();
  }
}

const DRAWN_SHAPE_STYLE = {
  color: '#4786ed',
  weight: 2,
  fillColor: '#4786ed',
  fillOpacity: 0.2,
};

function drawEllipse(ctx, projection, bounds, style, lineScale) {
  const [[south, west], [north, east]] = bounds;
  const nw = projection.toCanvas([north, west]);
  const se = projection.toCanvas([south, east]);

  ctx.beginPath();
  ctx.ellipse(
    (nw.x + se.x) / 2,
    (nw.y + se.y) / 2,
    Math.abs(se.x - nw.x) / 2,
    Math.abs(se.y - nw.y) / 2,
    0,
    0,
    Math.PI * 2,
  );

  ctx.fillStyle = rgba(style.fillColor, style.fillOpacity);
  ctx.fill();
  ctx.strokeStyle = style.color;
  ctx.lineWidth = style.weight * lineScale;
  ctx.stroke();
}

async function drawOverlays(ctx, map, projection, layers, iconScale) {
  const {
    markers = [],
    geotileMarkers = [],
    selectedId,
    locationShape,
    drawnShapes = [],
  } = layers;

  if (locationShape) {
    drawShape(
      ctx,
      projection,
      locationShape,
      LOCATION_OUTLINE_STYLE,
      iconScale,
    );
  }

  const shapeItems = markers.filter(item => item.shape);
  const ordered = [
    ...shapeItems.filter(item => item.id !== selectedId),
    ...shapeItems.filter(item => item.id === selectedId),
  ];

  ordered.forEach(item => {
    if (item.id === selectedId) {
      drawShape(
        ctx,
        projection,
        item.shape,
        POLYGON_FOCUS_CASING_STYLE,
        iconScale,
      );
      drawShape(ctx, projection, item.shape, POLYGON_FOCUS_STYLE, iconScale);
    } else {
      drawShape(ctx, projection, item.shape, POLYGON_DEFAULT_STYLE, iconScale);
    }
  });

  drawnShapes.forEach(shape => {
    if (shape.kind === 'ellipse') {
      drawEllipse(ctx, projection, shape.bounds, DRAWN_SHAPE_STYLE, iconScale);
    } else {
      drawShape(ctx, projection, shape.latLngs, DRAWN_SHAPE_STYLE, iconScale);
    }
  });

  geotileMarkers.forEach(tile => {
    const nw = projection.toCanvas([tile.bounds[1][0], tile.bounds[0][1]]);
    const se = projection.toCanvas([tile.bounds[0][0], tile.bounds[1][1]]);
    ctx.strokeStyle = CLUSTER_GRID_COLOR;
    ctx.lineWidth = iconScale;
    ctx.strokeRect(nw.x, nw.y, se.x - nw.x, se.y - nw.y);
  });

  geotileMarkers.forEach(tile => {
    const { fill, size } = clusterBadgeStyle(tile.count);
    const radius = (size / 2) * iconScale;
    const centre = projection.toCanvas(tile.center);

    ctx.beginPath();
    ctx.arc(centre.x, centre.y, radius, 0, Math.PI * 2);
    ctx.fillStyle = fill;
    ctx.fill();
    ctx.lineWidth = 2 * iconScale;
    ctx.strokeStyle = '#FFFFFF';
    ctx.stroke();

    ctx.fillStyle = '#1A1A1A';
    ctx.font = `bold ${12 * iconScale}px Tahoma, Verdana, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(tile.count.toLocaleString(), centre.x, centre.y);
  });

  // Hexagons stand in for shapes while zoomed out, exactly as on the map.
  const showHexes = map.getZoom() < HEX_ZOOM_THRESHOLD;
  const pinItems = markers
    .filter(item => !item.shape && item.position)
    .map(item => ({ id: item.id, at: item.position }));
  const hexItems = showHexes
    ? markers
        .filter(item => item.shape)
        .map(item => ({ id: item.id, at: hexAnchorFor(item) }))
        .filter(item => item.at != null)
    : [];

  if (pinItems.length === 0 && hexItems.length === 0) return;

  const [pin, selectedPin, hex, selectedHex] = await Promise.all([
    pinItems.length ? svgToImage(buildPinSvg(false)) : null,
    pinItems.length ? svgToImage(buildPinSvg(true)) : null,
    hexItems.length ? svgToImage(buildHexSvg(false)) : null,
    hexItems.length ? svgToImage(buildHexSvg(true)) : null,
  ]);

  const drawIcon = (image, latLng, size, anchor) => {
    const { x, y } = projection.toCanvas(latLng);
    ctx.drawImage(
      image,
      x - anchor[0] * iconScale,
      y - anchor[1] * iconScale,
      size[0] * iconScale,
      size[1] * iconScale,
    );
  };

  const paint = (items, image, selectedImage, size, anchor) => {
    items
      .filter(item => item.id !== selectedId)
      .forEach(item => drawIcon(image, item.at, size, anchor));
    // Selected last so it sits on top, as setZIndexOffset does on the map.
    items
      .filter(item => item.id === selectedId)
      .forEach(item => drawIcon(selectedImage, item.at, size, anchor));
  };

  paint(hexItems, hex, selectedHex, HEX_SIZE, HEX_ANCHOR);
  paint(pinItems, pin, selectedPin, PIN_SIZE, PIN_ANCHOR);
}

async function renderMapCanvas(map, basemap, layers) {
  const { pixelZoom, tileZoom } = chooseZooms(map);
  const projection = createProjection(map, pixelZoom);
  // Markers and stroke widths are authored in screen pixels, so scale them by
  // however much bigger the canvas is than the on-screen map.
  const iconScale = projection.width / Math.max(1, map.getSize().x);

  const canvas = document.createElement('canvas');
  canvas.width = projection.width;
  canvas.height = projection.height;
  const ctx = canvas.getContext('2d');

  // Neutral backdrop so any tile that fails to load reads as blank map rather
  // than transparent-turned-black in the PDF.
  ctx.fillStyle = '#F6F3EF';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  await drawBasemap(ctx, map, basemap, projection, tileZoom);
  await drawOverlays(ctx, map, projection, layers, iconScale);

  return canvas;
}

/**
 * Redraw the current map view as a flat image for the print sheet.
 *
 * @param {Object} options
 * @param {L.Map} options.map - The live Leaflet map.
 * @param {Object} options.basemap - The active entry from BASEMAPS.
 * @param {Array} options.markers - MapView's individual markers.
 * @param {Array} options.geotileMarkers - MapView's cluster markers.
 * @param {*} options.selectedId - Id of the selected marker, if any.
 * @param {Array|null} options.locationShape - Searched location's boundary.
 * @param {Array} options.drawnShapes - Drawn search area from DrawControl.
 * @returns {Promise<{ src: string, width: number, height: number }>}
 */
export async function renderMapImage({
  map,
  basemap,
  markers,
  geotileMarkers,
  selectedId,
  locationShape,
  drawnShapes,
}) {
  const canvas = await renderMapCanvas(map, basemap, {
    markers,
    geotileMarkers,
    selectedId,
    locationShape,
    drawnShapes,
  });

  return {
    src: canvas.toDataURL('image/jpeg', 0.92),
    width: canvas.width,
    height: canvas.height,
  };
}

export default renderMapImage;
